import { useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  formatProjectErrorMessage,
  updateProjectSchema,
} from "@/features/projects";
import { cn } from "@/lib/utils";

interface ProjectFormSubmission {
  title: string;
  genre: string;
  coverImageFile: File | null;
  removeCoverImage?: boolean;
}

type ProjectFormProps = {
  busy: boolean;
  initialCoverImageUrl?: string | null;
  initialGenre?: string;
  initialTitle?: string;
  layout?: "default" | "settings";
  onCoverPreviewClick?: () => void;
  onSubmit: (input: ProjectFormSubmission) => Promise<void>;
  submitLabel: string;
};

interface FieldErrors {
  genre?: string;
  title?: string;
}

export function ProjectForm({
  busy,
  initialCoverImageUrl = null,
  initialGenre = "",
  initialTitle = "",
  layout = "default",
  onCoverPreviewClick,
  onSubmit,
  submitLabel,
}: ProjectFormProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isSettingsLayout = layout === "settings";
  const [form, setForm] = useState<ProjectFormSubmission>({
    title: initialTitle,
    genre: initialGenre,
    coverImageFile: null,
    removeCoverImage: false,
  });
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    setForm({
      title: initialTitle,
      genre: initialGenre,
      coverImageFile: null,
      removeCoverImage: false,
    });
    setFieldErrors({});
    setSubmitError(null);
    setPreviewUrl(null);
  }, [initialCoverImageUrl, initialGenre, initialTitle]);

  useEffect(() => {
    if (!form.coverImageFile) {
      setPreviewUrl(null);
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(form.coverImageFile);
    setPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [form.coverImageFile]);

  const visibleCoverUrl = useMemo(() => {
    if (previewUrl) {
      return previewUrl;
    }

    if (form.removeCoverImage) {
      return null;
    }

    return initialCoverImageUrl;
  }, [form.removeCoverImage, initialCoverImageUrl, previewUrl]);

  function handleChooseCover() {
    fileInputRef.current?.click();
  }

  function handleRemoveCover() {
    setForm((current) => ({
      ...current,
      coverImageFile: null,
      removeCoverImage: true,
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFieldErrors({});
    setSubmitError(null);

    const parsed = updateProjectSchema.safeParse(form);

    if (!parsed.success) {
      const nextErrors: FieldErrors = {};

      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === "title" || field === "genre") {
          nextErrors[field] = issue.message;
        }
      }

      setFieldErrors(nextErrors);
      return;
    }

    try {
      await onSubmit({
        ...parsed.data,
        coverImageFile: parsed.data.coverImageFile ?? null,
      });
    } catch (error) {
      setSubmitError(formatProjectErrorMessage(error));
    }
  }

  const coverPreviewContent = visibleCoverUrl ? (
    <div
      className={cn(
        "mt-3 flex items-center gap-3 rounded-[20px] border p-3",
        isSettingsLayout
          ? "border-ink/10 bg-canvas/65"
          : "border-border/70 bg-background/80 shadow-sm",
      )}
    >
      <img
        alt={t("projects.form.cover")}
        className="h-16 w-16 shrink-0 rounded-2xl object-cover"
        src={visibleCoverUrl}
      />
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          {t("projects.form.coverReady")}
        </p>
        <p className="truncate text-sm font-medium text-foreground">
          {form.coverImageFile?.name ?? t("projects.form.coverStored")}
        </p>
      </div>
    </div>
  ) : null;

  return (
    <form className={cn(isSettingsLayout ? "space-y-8" : "space-y-5")} onSubmit={handleSubmit}>
      <div
        className={cn(
          "grid gap-5 sm:grid-cols-2",
          isSettingsLayout && "gap-x-8 gap-y-6 border-b border-ink/10 pb-8",
        )}
      >
        <div className="space-y-2">
          <Label
            className={cn(isSettingsLayout && "mb-0 text-base font-semibold text-ink")}
            htmlFor="project-title"
          >
            {t("projects.form.title")}
          </Label>
          <Input
            autoComplete="off"
            className={cn(
              isSettingsLayout
                && "rounded-none border-0 border-b border-ink/12 bg-transparent px-0 py-4 text-lg text-ink shadow-none placeholder:text-ink/35 focus:border-clay/45 focus:ring-0",
            )}
            id="project-title"
            name="title"
            onChange={(event) =>
              setForm((current) => ({ ...current, title: event.target.value }))
            }
            placeholder="Nebula Dominion"
            value={form.title}
          />
          {fieldErrors.title ? <FormMessage>{t(fieldErrors.title)}</FormMessage> : null}
        </div>

        <div className="space-y-2">
          <Label
            className={cn(isSettingsLayout && "mb-0 text-base font-semibold text-ink")}
            htmlFor="project-genre"
          >
            {t("projects.form.genre")}
          </Label>
          <Input
            autoComplete="off"
            className={cn(
              isSettingsLayout
                && "rounded-none border-0 border-b border-ink/12 bg-transparent px-0 py-4 text-lg text-ink shadow-none placeholder:text-ink/35 focus:border-clay/45 focus:ring-0",
            )}
            id="project-genre"
            name="genre"
            onChange={(event) =>
              setForm((current) => ({ ...current, genre: event.target.value }))
            }
            placeholder="Science fantasy"
            value={form.genre}
          />
          {fieldErrors.genre ? <FormMessage>{t(fieldErrors.genre)}</FormMessage> : null}
        </div>
      </div>

      <div
        className={cn(
          isSettingsLayout
            ? "space-y-5"
            : "rounded-[24px] border border-border/80 bg-secondary/55 p-4",
        )}
      >
        <div
          className={cn(
            "flex flex-col gap-4",
            "lg:flex-row lg:items-start lg:justify-between",
          )}
        >
          <div className="min-w-0 flex-1 space-y-2">
            <p
              className={cn(
                "text-sm font-semibold text-foreground",
                isSettingsLayout && "text-base font-semibold text-ink",
              )}
            >
              {t("projects.form.cover")}
            </p>
            <p
              className={cn(
                "text-sm text-muted-foreground",
                isSettingsLayout && "max-w-xl text-sm leading-6 text-ink/60",
              )}
            >
              {t("projects.form.coverHelp")}
            </p>
            {coverPreviewContent && onCoverPreviewClick ? (
              <button
                className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/40 focus-visible:ring-offset-2"
                onClick={onCoverPreviewClick}
                type="button"
              >
                {coverPreviewContent}
              </button>
            ) : coverPreviewContent}
          </div>

          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              accept="image/*"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setForm((current) => ({
                  ...current,
                  coverImageFile: file,
                  removeCoverImage: false,
                }));
              }}
              type="file"
            />
            <Button onClick={handleChooseCover} type="button" variant="outline">
              <ImagePlus className="h-4 w-4" />
              {visibleCoverUrl
                ? t("projects.form.coverReplace")
                : t("projects.form.coverChoose")}
            </Button>
            {visibleCoverUrl ? (
              <Button onClick={handleRemoveCover} type="button" variant="ghost">
                <Trash2 className="h-4 w-4" />
                {t("projects.form.removeCover")}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {submitError ? <FormMessage>{submitError}</FormMessage> : null}

      <div className={cn("flex justify-end", isSettingsLayout && "border-t border-ink/10 pt-6")}>
        <Button disabled={busy} size="lg" type="submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
